import {Paging} from '@app/modules/shared/models/paging';
import {Client} from '@app/modules/shared/models/client';
import {UserAbilityService} from '@app/services/user-ability.service';
import {AppNavigation, Impersonation, UserPreferences} from '@app/modules/shared/models/navigation';

export class User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  job_title: string;
  phone: string;
  website: string;
  comment: string;
  token: string;
  password_status: string;
  auth_source?: string;
  user_type: string;
  client_id: number;
  organization_id: number;
  client: Client;
  role_user: any;
  status: any;
  project_id: any;
  timezone: string;
  created_by: number;
  updated_by: number;
  profile_img: any;
  role_slug?: string;
  /** From the login API: `role.default_dashboard`, the path this user lands on after login. */
  default_dashboard?: string;
  /** Set by the server when the password is close to expiring; surfaced as a banner. */
  password_expiry_warning?: {
    expires_at: string;
    reason?: string;
  } | null;
  suspended_at?: string | null;
  suspended_reason?: string | null;
  locked_until?: string | null;
  /** 'service' marks an API-key principal: the one flag, never a boolean beside it. */
  account_type?: 'human' | 'service';
  last_authentication_at?: string | null;
  /** The menus the server resolved for this user; null when it could not send them. */
  navigation?: AppNavigation | null;
  /**
   * The server's abilities this user holds, as `<resource>.<action>` (Ability.js
   * PANEL_ABILITIES): only the granted ones are sent.
   */
  abilities?: string[];
  /**
   * The actor's own layout and bookmarks, as of the last session response. The live values
   * are NavigationStateService's, which writes them back here.
   */
  preferences?: UserPreferences;
  /** Who is really at the keyboard while impersonating; null otherwise. */
  impersonation?: Impersonation | null;

  /** Whether the user holds the server's rule `<resource>.<action>` (see `abilities`). */
  can(action: string, resource: string): boolean {
    const abilityService = new UserAbilityService();
    abilityService.setUser(this);
    return abilityService.can(action, resource);
  }

  /** Admins, and an organization's own manager: who edits the organization fields of its settings. */
  canManageOrganizationFields(): boolean {
    const abilityService = new UserAbilityService();
    abilityService.setUser(this);
    return abilityService.canManageOrganizationFields();
  }

  /** Configuring this organization's single sign-on: any organization's, or the user's own. */
  canManageOrganizationSso(organizationId: number | string | null | undefined): boolean {
    const abilityService = new UserAbilityService();
    abilityService.setUser(this);
    return abilityService.canManageOrganizationSso(organizationId);
  }

  isPasswordReadyForLogin(): boolean {
    return this.password_status === '0' || this.auth_source === 'sso';
  }
}

export class PaginatedUser {
  list: User[];
  paging: Paging;
}

export const RoleTypes = {
  SUPER_ADMIN: 'super-admin',
  ADMIN: 'admin',
  ORGANIZATION_MANAGER: 'client-organization-account-manager',
  PROJECT_MANAGER: 'project-manager',
  PROJECT_MANAGEMENT_LEAD: 'project-management-lead',
  MANAGER: 'client-account-manager',
  PLANNER: 'client-account-planner',
};

